import { ChangeEvent, useCallback, useState } from "react";
import { useBackOrNavigate } from "../../../../common/utils/navigateUtils";
import { useForm } from "react-hook-form";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { processError } from "../../../../common/utils/base_error";
import { zodResolver } from "@hookform/resolvers/zod";
import { ObraForm, ObraSchema } from "../schemas/Obras_informacoesgerais/obra";
import { useLocation } from "react-router";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { useObrasCommonReports } from "../components/hooks/obras_common_reports";
import { useObrasTopHeaderState } from "../components/hooks/obras_top_header_state";
import { AlvenariaEstruturalLista } from "../../domain/models/plano_alvenaria_estrutural_lista";
import { useAlvenariaEstrututalFilters } from "../hooks/Obras_alvenariaestrutural/filter";
import { ObraAlvenariaEstruturalForm, ObraAlvenariaEstruturalSchema } from "../schemas/Obras_alvenaria_estrutura/obra_alvenaria_estrutural";
import { evaluateParamValue } from "../../../../common/utils/params_utils";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { ObraHeaderInfo } from "../../domain/models/obra_header_info";
import { useAlvenariaEstruturalPagination } from "../hooks/Obras_alvenariaestrutural/pagination";


type LoadAlvenariaEstruturalFuncState = {
  IdObra?: number;
  page?: number;
  rowsPerPage?: number;
  dataReceberDe?: Date | null;
  dataReceberAte?: Date | null;
  Fornecedor?: string | null;
  TipoBloco?: string | null;
  NroNotaFiscal?: string | null;
};

//useObrasAlvenariaEstruturalViewModel
export const useObrasAlvenariaEstruturalViewModel = () => {
  const {
    obras: {
      createAlvenariaEstruturalServicosUseCase,
      getAllObrasAlvenariaEstruturalServicosUseCase,
      updateObraAlvenariaEstruturalUseCase,
      deleteObraAlvenariaEstrutural,
      getObraHeaderInfoUseCase,
      downloadAlvenariaEstruturaUseCase,
    }
  } = useDependencyInjection();

  const location = useLocation();

  const backOrNavigate = useBackOrNavigate();


  const {
    pushErrorNotification,
    pushInfoNotification,
    setPageLoading,
    setCustomLoading,
  } = usePageLoader();

  const debouncedRef = useCreateDebounceHandler(1500);

  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [idObra, setIdObra] = useState<number>();
  const [selectedObra, setSelectedObra] = useState<ObraHeaderInfo>();
  const [alvenariaEstruturalLista, setAlvenariaEstruturalLista] = useState<AlvenariaEstruturalLista[]>([]);
  const [selectedAlvenariaEstrutura, setSelectedAlvenariaEstrutura] = useState<AlvenariaEstruturalLista | undefined>(undefined);
  const [alvenariaEstruturaModal, setAlvenariaEstruturaModal] = useState<boolean>(false);

  //ARQUIVOS
  const [alvenariaEstruturalDeleteModal, setAlvenariaEstruturalDeleteModal] = useState<boolean>(false);

  const hookForm = useForm<ObraForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: { tipo: 0 },
    resolver: zodResolver(ObraSchema),
  });
  const hookFormArquivo = useForm<ObraAlvenariaEstruturalForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(ObraAlvenariaEstruturalSchema),
  });

  const obrasTopHeaderState = useObrasTopHeaderState();

  const [obrasReportState, initializeReportState] = useObrasCommonReports({ idObra });

  const filters = useAlvenariaEstrututalFilters();
  const pagination = useAlvenariaEstruturalPagination();
  // #region Funções de carregamento

  const loadAlvenariaEstruturaListaState = useCallback(async (funcState?: LoadAlvenariaEstruturalFuncState) => {
    try {
      const filterID_OBRA = evaluateParamValue(funcState?.IdObra, filters.IdObra);
      const filteredDataReceberDe = evaluateParamValue(funcState?.dataReceberDe, filters.dataReceberDe);
      const filteredDataReceberAte = evaluateParamValue(funcState?.dataReceberAte, filters.dataReceberAte);
      const filteredFornecedor = evaluateParamValue(funcState?.Fornecedor, filters.Fornecedor);
      const filteredTipoBloco = evaluateParamValue(funcState?.TipoBloco, filters.TipoBloco);
      const filteredNroNotaFiscal = evaluateParamValue(funcState?.NroNotaFiscal, filters.NroNotaFiscal);

      if (filterID_OBRA) {
        const filteredPage = funcState?.page ?? pagination.page;
        const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;
        await getAllObrasAlvenariaEstruturalServicosUseCase.getAllObrasAlvenariaEstrutural(
          {
            IdObra: filterID_OBRA,
            dataReceberAte: filteredDataReceberAte,
            dataReceberDe: filteredDataReceberDe,
            Fornecedor: filteredFornecedor ?? "",
            TipoBloco: filteredTipoBloco ?? "",
            NroNotaFiscal: filteredNroNotaFiscal ?? ""
          },
          filteredPage,
          filteredRowsPerPage
        );
        setAlvenariaEstruturalLista(getAllObrasAlvenariaEstruturalServicosUseCase.items);
        pagination.setRowsPerPage(getAllObrasAlvenariaEstruturalServicosUseCase.pageSize);
        pagination.setTotalItems(getAllObrasAlvenariaEstruturalServicosUseCase.totalItems);

      }

    } catch (e: unknown) {
      throw new Error("Falha ao buscar alvenaria estrutural!");
    }
  }, [filters, getAllObrasAlvenariaEstruturalServicosUseCase, pagination]);


  const loadObraHeaderInfo = useCallback(async (id?: number) => {
    try {
      const filteredIdObra = id ?? idObra;
      const response = await getObraHeaderInfoUseCase.getObraHeaderInfo(filteredIdObra);
      setSelectedObra(response);
    } catch (e: unknown) {
      setSelectedObra(undefined);
      throw new Error('Falha ao buscar dados da obra!');
    }
  }, [idObra, getObraHeaderInfoUseCase,]);

  // #endregion Funções de carregamento


  // #region useEffect

  usePageStartBootstrap(async () => {
    try {
      const params = new URLSearchParams(location.search);
      const locationId = params.get('id') ?? null;
      const locationIdObra = locationId ? Number(locationId) : undefined;
      const jobs = [];

      setIdObra(locationIdObra);

      if (locationIdObra) {
        jobs.push(initializeReportState(locationIdObra));
        jobs.push(loadAlvenariaEstruturaListaState({ IdObra: locationIdObra }))
        jobs.push(loadObraHeaderInfo(locationIdObra));
      }
      await Promise.all(jobs);
    } catch (e: unknown) {
      setErrorMessage(processError(e, 'Falha ao buscar dados da pagina!')[0]);
    }
  });

  // #endregion useEffect


  // #region Eventos

  const handlePopperOptionSelect = useCallback(async (idOption: number) => {
    switch (idOption) {
      case 1:
        setPageLoading(true);
        setTimeout(() => {
          setIdObra(undefined);
          setSelectedObra(undefined);
          setErrorMessage(undefined);
          setAlvenariaEstruturalLista([]);
          setSelectedAlvenariaEstrutura(undefined);
          setAlvenariaEstruturaModal(false);

          hookForm.reset();
          setTimeout(() => setPageLoading(false), 500);
        }, 0);

        break;

      case 2:
        if (idObra) {
          // setObraDeleteModal(true);
        }
        break;
    }
  }, [idObra, hookForm, setPageLoading]);

  const handleReload = useCallback(async () => {
    try {
      const jobs = [];
      setLoading(true);
      if (idObra) {
        jobs.push(initializeReportState(idObra));
        jobs.push(loadAlvenariaEstruturaListaState({ IdObra: idObra }))
        jobs.push(loadObraHeaderInfo(idObra));
      }
      await Promise.all(jobs);
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [idObra, initializeReportState, loadAlvenariaEstruturaListaState, loadObraHeaderInfo, pushErrorNotification]);

  const handleBack = useCallback(() => {
    backOrNavigate(-1, { alternativeRoute: '/Obras_lista' });
  }, [
    backOrNavigate,
  ]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      if (idObra) {
        pagination.setPage(page + 1);;
        await loadAlvenariaEstruturaListaState({
          page: page + 1,
          IdObra: idObra
        });
      }
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [idObra, loadAlvenariaEstruturaListaState, pagination, pushErrorNotification]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      if (idObra) {
        setLoading(true);
        pagination.setPage(1);
        pagination.setRowsPerPage(rowsPerPage);

        await loadAlvenariaEstruturaListaState({
          page: 1, rowsPerPage,
          IdObra: idObra
        });
      }
    } catch (e: unknown) {
      pushErrorNotification('Erro', processError(e, 'Falha ao buscar dados da pagina!'));
    } finally {
      setLoading(false);
    }
  }, [idObra, loadAlvenariaEstruturaListaState, pagination, pushErrorNotification]);

  // #endregion Eventos

  // #region Estrutura Treinamento
  const handleCadastroAlvenariaEstruturaOpenState = useCallback((IdAlvenariaObra: number | null, openState: boolean) => {
    if (openState) {
      if (IdAlvenariaObra) {
        setSelectedAlvenariaEstrutura(alvenariaEstruturalLista.find(x => x.IdAlvenariaObra === IdAlvenariaObra));
      } else {
        setSelectedAlvenariaEstrutura(undefined);
      }
    }
    setAlvenariaEstruturaModal(openState);
  }, [alvenariaEstruturalLista]);

  const handleAlvenariaEstruturalSubmit = useCallback(async (form: ObraAlvenariaEstruturalForm) => {
    setSaving(true);
    if (selectedObra && idObra) {
      try {
        const aprovado = form.Aprovado === 1 ? true : false;
        if (selectedAlvenariaEstrutura) {
          await updateObraAlvenariaEstruturalUseCase.updateAlvenariaEstrutural(
            idObra,
            selectedAlvenariaEstrutura.IdAlvenariaObra,
            form.TipoBloco ?? '',
            form.Dimensao ?? '',
            form.Fornecedor ?? '',
            form.LocalAplicacao ?? '',
            String(form.QtdeRecibida) ?? "",
            form.NrAmostras ?? '',
            form.NroNotaFiscal ?? '',
            form.NomeLaboratorio ?? '',
            form.EspecificacaoArgamassa ?? '',
            form.EnsaiosSolicitados ?? '',
            form.DataRecebimento,
            form.DataEnvio ?? null,
            form.ResponsavelCadastrado ?? false,
            form.DescResponsavel ?? "",
            form.IdFuncionario ?? null,
            form.Observacao ?? '',
            form.Ensaio1 ?? '',
            form.Ensaio2 ?? '',
            form.NroAmostras1 ?? '',
            form.NroAmostras2 ?? '',
            form.RbIdade1 ?? '',
            form.RbIdade2 ?? '',
            form.RbIdade3 ?? '',
            form.RbResultado1 ?? '',
            form.RbResultado2 ?? '',
            form.RbResultado3 ?? '',
            form.RaIdade1 ?? '',
            form.RaIdade2 ?? '',
            form.RaIdade3 ?? '',
            form.RaResultado1 ?? '',
            form.RaResultado2 ?? '',
            form.RaResultado3 ?? '',
            aprovado,
            form.Arquivo1 ?? null,
            form.Arquivo2 ?? null,
            form.NrCorpoProva ?? null,
            form.ResistenciaSolicitada ?? "",
            form.limparArquivo1,
            form.limparArquivo2,
          );
          pushInfoNotification("Sucesso", "Atualizado com sucesso!", { isAutoFinish: true });
        } else {
          await createAlvenariaEstruturalServicosUseCase.createAlvenariaEstrutural(
            idObra,
            form.TipoBloco ?? '',
            form.Dimensao ?? '',
            form.Fornecedor ?? '',
            form.LocalAplicacao ?? '',
            String(form.QtdeRecibida) ?? "",
            form.NrAmostras ?? '',
            form.NroNotaFiscal ?? '',
            form.NomeLaboratorio ?? '',
            form.EspecificacaoArgamassa ?? '',
            form.EnsaiosSolicitados ?? '',
            form.DataRecebimento,
            form.DataEnvio ?? null,
            form.ResponsavelCadastrado ?? false,
            form.DescResponsavel ?? "",
            form.IdFuncionario ?? null,
            form.Observacao ?? '',
            form.Ensaio1 ?? '',
            form.Ensaio2 ?? '',
            form.NroAmostras1 ?? '',
            form.NroAmostras2 ?? '',
            form.RbIdade1 ?? '',
            form.RbIdade2 ?? '',
            form.RbIdade3 ?? '',
            form.RbResultado1 ?? '',
            form.RbResultado2 ?? '',
            form.RbResultado3 ?? '',
            form.RaIdade1 ?? '',
            form.RaIdade2 ?? '',
            form.RaIdade3 ?? '',
            form.RaResultado1 ?? '',
            form.RaResultado2 ?? '',
            form.RaResultado3 ?? '',
            aprovado,
            form.Arquivo1 ?? null,
            form.Arquivo2 ?? null,
            form.NrCorpoProva ?? null,
            form.ResistenciaSolicitada ?? '',
          );
          pushInfoNotification("Sucesso", "Criado com sucesso!", { isAutoFinish: true });
        }
        await loadAlvenariaEstruturaListaState({ IdObra: idObra });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao criar alvenaria estrutural!'));
      } finally {
        setSaving(false);
        setAlvenariaEstruturaModal(false);
      }
    }
  }, [
    selectedObra,
    idObra,
    selectedAlvenariaEstrutura,
    updateObraAlvenariaEstruturalUseCase,
    createAlvenariaEstruturalServicosUseCase,
    loadAlvenariaEstruturaListaState,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleDeleteOAlvenariaEstruturalModalOpenState = useCallback((IdAlvenariaObra: number | null, openState: boolean) => {
    if (openState) {
      if (IdAlvenariaObra && alvenariaEstruturalLista) {
        setSelectedAlvenariaEstrutura(alvenariaEstruturalLista.find(x => x.IdAlvenariaObra === IdAlvenariaObra));
      } else {
        setSelectedAlvenariaEstrutura(undefined);
      }
    }
    setAlvenariaEstruturalDeleteModal(openState);
  }, [alvenariaEstruturalLista]);

  const handleDeleteConfirmaAlvenariaEstrutural = useCallback(async () => {
    if (selectedAlvenariaEstrutura && idObra) {
      try {
        setSaving(true);
        await deleteObraAlvenariaEstrutural.deleteObraAlvenariaEstrutural(selectedAlvenariaEstrutura.IdAlvenariaObra, idObra);
        await loadAlvenariaEstruturaListaState({ IdObra: idObra });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao excluir informações!'));
      } finally {
        setSaving(false);
        setAlvenariaEstruturalDeleteModal(false);
      }
    }
  }, [selectedAlvenariaEstrutura, idObra, deleteObraAlvenariaEstrutural, loadAlvenariaEstruturaListaState, pushErrorNotification]);
  // #endregion

  //#region ARQUIVOS

  const handleDownload = useCallback(async (idAlvenariaObra: number, nroArquivo: number, nomeArquivo: string) => {
    if (!saving) {
      try {
        setCustomLoading(true);
        await downloadAlvenariaEstruturaUseCase
          .downloadAlvenariaEstrutura(idAlvenariaObra, nroArquivo, nomeArquivo);
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao fazer download do arquivo!'));
      } finally {
        setCustomLoading(false);
      }
    };
  }, [
    saving,
    downloadAlvenariaEstruturaUseCase,
    pushErrorNotification,
    setCustomLoading,
  ]);

  const handleFileChange = useCallback((nomeArquivo: string, limparArquivos: string, e: ChangeEvent<HTMLInputElement>, onChange: (e: any) => void) => {
    if (!saving) {
      hookFormArquivo.setValue(limparArquivos as any, false);
      hookFormArquivo.setValue(nomeArquivo as any, e.target.files?.item(0)?.name ?? '');
      onChange(e.target.files?.item(0));
    }
  }, [saving, hookFormArquivo,]);

  //#endregion


  //#region Pesquisa
  const handleDataReceberRangeChange = useDebouncedCallback(debouncedRef,
    (dataReceberDe: Date | null, dataReceberAte: Date | null) => {
      filters.setFilters({ dataReceberDe });
      filters.setFilters({ dataReceberAte });

      return async () => {
        try {
          setLoading(true);
          pagination.setPage(1);
          await loadAlvenariaEstruturaListaState({ dataReceberDe, dataReceberAte, IdObra: idObra, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [loadAlvenariaEstruturaListaState, filters]
  );

  const handleFornecedorChange = useDebouncedCallback(debouncedRef,
    (Fornecedor: string | null) => {
      filters.setFilters({ Fornecedor });
      return async () => {
        try {
          setLoading(true);
          pagination.setPage(1);
          await loadAlvenariaEstruturaListaState({ Fornecedor, IdObra: idObra, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [loadAlvenariaEstruturaListaState, filters]
  );

  const handleTipoBlocoChange = useDebouncedCallback(debouncedRef,
    (TipoBloco: string | null) => {
      filters.setFilters({ TipoBloco });
      return async () => {
        try {
          setLoading(true);
          pagination.setPage(1);
          await loadAlvenariaEstruturaListaState({ TipoBloco, IdObra: idObra, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [loadAlvenariaEstruturaListaState, filters]
  );

  const handleNroNotaFiscalChange = useDebouncedCallback(debouncedRef,
    (NroNotaFiscal: string | null) => {
      filters.setFilters({ NroNotaFiscal });
      return async () => {
        try {
          setLoading(true);
          pagination.setPage(1);
          await loadAlvenariaEstruturaListaState({ NroNotaFiscal, IdObra: idObra, page: 1 });
        } catch (e: unknown) {
          pushErrorNotification("Error", processError(e, "Falha ao buscar contas a receber!"));
        } finally {
          setLoading(false);
        }
      };
    },
    [loadAlvenariaEstruturaListaState, filters]
  );

  const handleClearFilters = useCallback(async () => {
    try {
      setLoading(true);
      filters.clearFilters();
      pagination.setPage(1);
      await loadAlvenariaEstruturaListaState({ IdObra: idObra });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar contas a receber!'));
    } finally {
      setLoading(false);
    }
  }, [filters, pagination, loadAlvenariaEstruturaListaState, idObra, pushErrorNotification]);

  //#endregion

  return {
    loading,
    saving,
    errorMessage,
    hookForm,
    idObra,
    selectedObra: selectedObra ?? null,
    obrasTopHeaderState,
    obrasReportState,
    pagination,
    filters,
    alvenariaEstruturaModal,
    selectedAlvenariaEstrutura,
    alvenariaEstruturalDeleteModal,
    alvenariaEstruturalLista: alvenariaEstruturalLista,
    handlePopperOptionSelect,
    handleReload,
    handleBack,
    handleCadastroAlvenariaEstruturaOpenState,
    handleAlvenariaEstruturalSubmit,
    handleDownload,
    handleFileChange,
    handleRowsPerPageChange,
    handlePageChange,
    handleDeleteOAlvenariaEstruturalModalOpenState,
    handleDeleteConfirmaAlvenariaEstrutural,
    handleDataReceberRangeChange,
    handleFornecedorChange,
    handleTipoBlocoChange,
    handleNroNotaFiscalChange,
    handleClearFilters,
  };
}